import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import ThemeToggle from './ThemeToggle';
import { Brain, ArrowLeft, BookOpen, MessageSquare, LogOut, User, Settings } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { ChatPanel } from '@/components/chat/ChatPanel';
import { AIChatSignInModal } from '@/components/chat/AIChatSignInModal';
import { CustomSession } from '@/types/session';
import { useAuth } from '@/contexts/AuthContext';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

interface HeaderProps {
  onStartTour: () => void;
  onCreateCustomSession?: (session: CustomSession) => void;
  onSelectPreset?: (presetId: string) => void;
  showChat?: boolean;
}

const Header: React.FC<HeaderProps> = ({ 
  onStartTour, 
  onCreateCustomSession, 
  onSelectPreset,
  showChat = true 
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const isDashboard = location.pathname === '/';
  const [isChatOpen, setIsChatOpen] = React.useState(false);
  const [isSignInModalOpen, setIsSignInModalOpen] = React.useState(false);
  const { user, signOut } = useAuth();
  
  const handleSignOut = async () => {
    try {
      await signOut();
      navigate('/');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const handleChatClick = () => {
    if (!user) {
      setIsSignInModalOpen(true);
      return;
    }
    setIsChatOpen(true);
  };
  
  return (
    <header className="w-full py-6 animate-fade-in">
      <div className="container mx-auto px-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Link to="/">
              <img src="/logo.svg" alt="BinauralFlow" className="h-14 w-14" />
            </Link>
            <Link to="/">
              <h1 className="text-xl font-medium tracking-tight">BinauralFlow</h1>
            </Link>
          </div>
          <nav className="hidden md:block">
            <ul className="flex items-center gap-8">
              {!isDashboard && (
                <li>
                  <Link 
                    to="/" 
                    className="text-sm font-medium text-primary hover:text-primary/80 transition-colors flex items-center gap-1"
                  >
                    <ArrowLeft className="h-4 w-4" />
                    Back to Dashboard
                  </Link>
                </li>
              )}
              {user && (
                <>
                  <li>
                    <Link 
                      to="/history" 
                      className={`text-sm font-medium ${
                        location.pathname === '/history' 
                          ? 'text-primary' 
                          : 'text-muted-foreground hover:text-foreground'
                      } transition-colors`}
                    >
                      Sessions
                    </Link>
                  </li>
                  <li>
                    <Link 
                      to="/favorites" 
                      className={`text-sm font-medium ${
                        location.pathname === '/favorites' 
                          ? 'text-primary' 
                          : 'text-muted-foreground hover:text-foreground'
                      } transition-colors`}
                    >
                      Favorites
                    </Link>
                  </li>
                </>
              )}
            </ul>
          </nav>
          <div className="flex items-center gap-2">
            {showChat && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-11 w-11 relative"
                    onClick={handleChatClick}
                  >
                    <MessageSquare className="h-6 w-6" />
                    <span className="absolute -top-1 -right-1 text-[10px] font-medium bg-primary/10 text-primary px-1.5 py-0.5 rounded-full">
                      AI
                    </span>
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  {user ? 'Chat with AI Assistant' : 'Sign in to chat with AI Assistant'}
                </TooltipContent>
              </Tooltip>
            )}
            <Tooltip>
              <TooltipTrigger asChild>
                <Link to="/help">
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-11 w-11"
                  >
                    <BookOpen className="h-6 w-6" />
                  </Button>
                </Link>
              </TooltipTrigger>
              <TooltipContent>
                Help Center
              </TooltipContent>
            </Tooltip>
            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" className="h-11 w-11">
                    <User className="h-6 w-6" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuLabel>My Account</DropdownMenuLabel>
                  <DropdownMenuItem asChild>
                    <Link to="/profile">
                      <Settings className="mr-2 h-4 w-4" />
                      My Profile
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleSignOut}>
                    <LogOut className="mr-2 h-4 w-4" />
                    Sign Out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <div className="flex items-center gap-2">
                <Link to="/auth/signin">
                  <Button variant="ghost">Sign In</Button>
                </Link>
                <Link to="/auth/signup">
                  <Button>Sign Up</Button>
                </Link>
              </div>
            )}
            <ThemeToggle />
          </div>
        </div>
      </div>
      {user && showChat && (
        <ChatPanel 
          isOpen={isChatOpen} 
          onOpenChange={setIsChatOpen}
          onStartCustomSession={onCreateCustomSession}
          onStartPreset={onSelectPreset}
        />
      )}
      <AIChatSignInModal 
        isOpen={isSignInModalOpen}
        onOpenChange={setIsSignInModalOpen}
      />
    </header>
  );
};

export default Header;
